// ==============================================================================
// CLIENTE HTTP DESACOPLADO DA API ADMINISTRATIVA (FRONTEND)
// ==============================================================================
// Regra de ouro de arquitetura:
// O frontend NÃO importa nem executa clientes SQL diretos, service_role ou
// credenciais de banco. Em produção, dispara chamadas HTTP autenticadas via PostgREST
// com token JWT do usuário administrativo ativo e anon key pública.
// Em desenvolvimento local / testes, utiliza um adapter mock explícito de interface.
// ==============================================================================

import type { CommercialStatus, OverallTierKey, CategoryScores } from '@/domains/admin/types';
import { MOCK_LEADS } from '@/mocks/mockData';
import { supabase } from '@/lib/supabase';

export interface LeadHistoryEntry {
  id: string;
  submissionId: string;
  leadId?: string;
  fromStatus?: string;
  toStatus: CommercialStatus;
  notes?: string;
  changedBy: string;
  createdAt: string;
  timestamp: string;
  field?: 'commercial_status' | 'notes';
  oldValue?: string;
  newValue?: string;
}

export interface AdminLeadItem {
  id: string;
  publicToken: string;
  name: string;
  email: string;
  phone: string;
  commercialStatus: CommercialStatus;
  notes?: string;
  origin?: string;
  utmCampaign?: string;
  leadName: string;
  leadEmail: string;
  leadPhone: string;
  companyName: string;
  companySize: string;
  leadStatus: CommercialStatus;
  overallScore: number;
  overallTierKey: OverallTierKey;
  overallTierLabel: string;
  categoryScores: CategoryScores;
  primaryPriorityKey: string;
  primaryPriorityName: string;
  secondaryPriorityKey?: string;
  hasRetentionRisk: boolean;
  source?: string;
  medium?: string;
  campaign?: string;
  createdAt: string;
  completedAt: string;
  history?: LeadHistoryEntry[];
  latestNote?: string;
}

export interface UpdateLeadStatusPayload {
  leadId: string;
  status: CommercialStatus;
}

export interface CreateLeadNotePayload {
  leadId: string;
  currentStatus: CommercialStatus;
  notes: string;
}

export interface CreateAuditLogPayload {
  action: 'export_csv' | 'view_lead' | 'update_status' | 'update_note';
  resourceType: 'submission' | 'lead_status_history';
  resourceId?: string;
  metadata?: Record<string, unknown>;
}

export interface AdminApiClient {
  getLeads: () => Promise<AdminLeadItem[]>;
  updateLeadStatus: (payload: UpdateLeadStatusPayload) => Promise<AdminLeadItem>;
  createLeadNote: (payload: CreateLeadNotePayload) => Promise<LeadHistoryEntry>;
  recordAuditLog: (payload: CreateAuditLogPayload) => Promise<{ success: boolean }>;
}

// ------------------------------------------------------------------------------
// Mapeador de Dados de Submissão PostgREST para o Modelo de Domínio AdminLeadItem
// ------------------------------------------------------------------------------
interface PostgrestScore {
  overall_score: number;
  overall_tier_key: string;
  overall_tier_label: string;
  score_positioning: number;
  score_founder_authority: number;
  score_digital_assets: number;
  score_distribution: number;
  score_commercial_conversation: number;
  score_customer_experience: number;
  primary_priority_key: string;
  primary_priority_name: string;
  secondary_priority_key?: string | null;
  secondary_priority_name?: string | null;
  has_retention_risk: boolean;
}

interface PostgrestHistory {
  id: string;
  submission_id: string;
  from_status?: string | null;
  to_status: CommercialStatus;
  notes?: string | null;
  changed_by?: string | null;
  created_at: string;
}

interface PostgrestSubmission {
  id: string;
  public_token: string;
  lead_name: string;
  lead_email: string;
  lead_phone: string;
  company_name: string;
  company_size?: string;
  lead_status: CommercialStatus;
  created_at: string;
  completed_at: string;
  source?: string | null;
  medium?: string | null;
  campaign?: string | null;
  scores?: PostgrestScore | PostgrestScore[] | null;
  lead_status_history?: PostgrestHistory[] | null;
}

function mapSubmissionToAdminLead(row: PostgrestSubmission): AdminLeadItem {
  const score = Array.isArray(row.scores) ? row.scores[0] : row.scores;
  const historyList = Array.isArray(row.lead_status_history)
    ? row.lead_status_history
    : [];

  const sortedHistory = [...historyList].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  const mappedHistory: LeadHistoryEntry[] = sortedHistory.map((h) => ({
    id: h.id,
    submissionId: h.submission_id,
    leadId: h.submission_id,
    fromStatus: h.from_status || undefined,
    toStatus: h.to_status,
    notes: h.notes || undefined,
    changedBy: h.changed_by || 'Israel Costa',
    createdAt: h.created_at,
    timestamp: h.created_at,
    field: h.notes ? 'notes' : 'commercial_status',
    oldValue: h.from_status || undefined,
    newValue: h.notes || h.to_status,
  }));

  const latestNoteEntry = sortedHistory.find((h) => h.notes && h.notes.trim() !== '');
  const latestNote = latestNoteEntry?.notes || undefined;

  return {
    id: row.id,
    publicToken: row.public_token,
    name: row.lead_name,
    email: row.lead_email,
    phone: row.lead_phone,
    commercialStatus: row.lead_status,
    notes: latestNote,
    origin: row.source || undefined,
    utmCampaign: row.campaign || undefined,

    leadName: row.lead_name,
    leadEmail: row.lead_email,
    leadPhone: row.lead_phone,
    companyName: row.company_name,
    companySize: row.company_size || 'Não informado',
    leadStatus: row.lead_status,
    overallScore: score?.overall_score ?? 0,
    overallTierKey: (score?.overall_tier_key as OverallTierKey) || 'building',
    overallTierLabel: score?.overall_tier_label || 'Presença em construção',
    categoryScores: {
      positioning: score?.score_positioning ?? 0,
      founder_authority: score?.score_founder_authority ?? 0,
      digital_assets: score?.score_digital_assets ?? 0,
      distribution: score?.score_distribution ?? 0,
      commercial_conversation: score?.score_commercial_conversation ?? 0,
      customer_experience: score?.score_customer_experience ?? 0,
    },
    primaryPriorityKey: score?.primary_priority_key || 'positioning',
    primaryPriorityName: score?.primary_priority_name || 'Posicionamento',
    secondaryPriorityKey: score?.secondary_priority_key || undefined,
    hasRetentionRisk: Boolean(score?.has_retention_risk),
    source: row.source || undefined,
    medium: row.medium || undefined,
    campaign: row.campaign || undefined,
    createdAt: row.created_at,
    completedAt: row.completed_at || row.created_at,
    history: mappedHistory,
    latestNote,
  };
}

// ------------------------------------------------------------------------------
// Adapter Mock Explícito para Desenvolvimento Local e Testes de Interface
// ------------------------------------------------------------------------------
class MockAdminApiAdapter implements AdminApiClient {
  private inMemoryLeads: AdminLeadItem[];

  constructor() {
    this.inMemoryLeads = MOCK_LEADS.map((l) => ({
      id: l.id,
      publicToken: l.publicToken,
      name: l.name,
      email: l.email,
      phone: l.phone,
      commercialStatus: l.commercialStatus,
      notes: l.notes,
      origin: l.origin,
      utmCampaign: l.utmCampaign,
      leadName: l.name,
      leadEmail: l.email,
      leadPhone: l.phone,
      companyName: l.companyName,
      companySize: '2 a 5 pessoas',
      leadStatus: l.commercialStatus,
      overallScore: l.overallScore,
      overallTierKey: l.overallTierKey,
      overallTierLabel: 'Presença em construção',
      categoryScores: l.categoryScores,
      primaryPriorityKey: l.primaryPriorityKey,
      primaryPriorityName: l.primaryPriorityKey.replace('_', ' '),
      hasRetentionRisk: l.hasRetentionRisk,
      source: l.origin,
      campaign: l.utmCampaign,
      createdAt: l.createdAt,
      completedAt: l.createdAt,
      latestNote: l.notes,
      history: (l.history || []).map((h) => ({
        id: h.id,
        submissionId: h.leadId,
        leadId: h.leadId,
        fromStatus: h.oldValue,
        toStatus: h.newValue as CommercialStatus,
        notes: h.field === 'notes' ? h.newValue : undefined,
        changedBy: h.changedBy,
        createdAt: h.timestamp,
        timestamp: h.timestamp,
        field: h.field,
        oldValue: h.oldValue,
        newValue: h.newValue,
      })),
    }));
  }

  async getLeads(): Promise<AdminLeadItem[]> {
    await new Promise((res) => setTimeout(res, 100));
    return JSON.parse(JSON.stringify(this.inMemoryLeads));
  }

  async updateLeadStatus(payload: UpdateLeadStatusPayload): Promise<AdminLeadItem> {
    await new Promise((res) => setTimeout(res, 100));
    const lead = this.inMemoryLeads.find((l) => l.id === payload.leadId);
    if (!lead) {
      throw new Error(`Lead ${payload.leadId} não encontrado.`);
    }

    const oldStatus = lead.leadStatus;
    lead.leadStatus = payload.status;
    lead.commercialStatus = payload.status;

    const newHistoryEntry: LeadHistoryEntry = {
      id: `hist-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      submissionId: lead.id,
      leadId: lead.id,
      fromStatus: oldStatus,
      toStatus: payload.status,
      notes: `Transição de status comercial: ${oldStatus} -> ${payload.status}`,
      changedBy: 'Israel Costa',
      createdAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      field: 'commercial_status',
      oldValue: oldStatus,
      newValue: payload.status,
    };

    lead.history = [newHistoryEntry, ...(lead.history || [])];
    return JSON.parse(JSON.stringify(lead));
  }

  async createLeadNote(payload: CreateLeadNotePayload): Promise<LeadHistoryEntry> {
    await new Promise((res) => setTimeout(res, 100));
    const lead = this.inMemoryLeads.find((l) => l.id === payload.leadId);
    if (!lead) {
      throw new Error(`Lead ${payload.leadId} não encontrado.`);
    }

    lead.latestNote = payload.notes;
    lead.notes = payload.notes;

    const newHistoryEntry: LeadHistoryEntry = {
      id: `hist-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      submissionId: lead.id,
      leadId: lead.id,
      fromStatus: lead.leadStatus,
      toStatus: payload.currentStatus,
      notes: payload.notes,
      changedBy: 'Israel Costa',
      createdAt: new Date().toISOString(),
      timestamp: new Date().toISOString(),
      field: 'notes',
      oldValue: lead.notes,
      newValue: payload.notes,
    };

    lead.history = [newHistoryEntry, ...(lead.history || [])];
    return newHistoryEntry;
  }

  async recordAuditLog(_payload: CreateAuditLogPayload): Promise<{ success: boolean }> {
    await new Promise((res) => setTimeout(res, 30));
    return { success: true };
  }
}

// ------------------------------------------------------------------------------
// Cliente HTTP Desacoplado para Produção (PostgREST Autenticado com Token JWT)
// ------------------------------------------------------------------------------
const POSTGREST_SELECT_FIELDS =
  'id,public_token,lead_name,lead_email,lead_phone,company_name,company_size,lead_status,created_at,completed_at,source,medium,campaign,' +
  'scores(overall_score,overall_tier_key,overall_tier_label,score_positioning,score_founder_authority,score_digital_assets,score_distribution,score_commercial_conversation,score_customer_experience,primary_priority_key,primary_priority_name,secondary_priority_key,secondary_priority_name,has_retention_risk),' +
  'lead_status_history(id,submission_id,from_status,to_status,notes,changed_by,created_at)';

class HttpAdminApiClient implements AdminApiClient {
  private baseUrl: string;
  private anonKey: string;
  private getAccessToken: () => Promise<string | null>;

  constructor(options?: {
    baseUrl?: string;
    anonKey?: string;
    getAccessToken?: () => Promise<string | null>;
  }) {
    this.baseUrl =
      options?.baseUrl ||
      (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_URL) ||
      '';
    this.anonKey =
      options?.anonKey ||
      (typeof import.meta !== 'undefined' && import.meta.env?.VITE_SUPABASE_ANON_KEY) ||
      '';
    this.getAccessToken =
      options?.getAccessToken ||
      (async () => {
        if (typeof supabase !== 'undefined') {
          const { data } = await supabase.auth.getSession();
          return data.session?.access_token || null;
        }
        return null;
      });
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.getAccessToken();
    if (!token) {
      throw new Error('Acesso negado: Sessão de administrador não autenticada.');
    }
    return {
      'Content-Type': 'application/json',
      apikey: this.anonKey,
      Authorization: `Bearer ${token}`,
    };
  }

  async getLeads(): Promise<AdminLeadItem[]> {
    const headers = await this.getAuthHeaders();
    const url = `${this.baseUrl}/rest/v1/submissions?select=${POSTGREST_SELECT_FIELDS}&order=created_at.desc`;
    const res = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new Error('Acesso negado: Perfil sem permissão de administrador.');
      }
      throw new Error(`Erro ao carregar leads (HTTP ${res.status}).`);
    }

    const data: PostgrestSubmission[] = await res.json();
    return (data || []).map(mapSubmissionToAdminLead);
  }

  async updateLeadStatus(payload: UpdateLeadStatusPayload): Promise<AdminLeadItem> {
    const headers = await this.getAuthHeaders();
    headers['Prefer'] = 'return=representation';

    const url = `${this.baseUrl}/rest/v1/submissions?id=eq.${payload.leadId}&select=${POSTGREST_SELECT_FIELDS}`;
    const res = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ lead_status: payload.status }),
    });

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new Error('Acesso negado: Perfil sem permissão para atualizar status.');
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Erro ao atualizar status (HTTP ${res.status}).`);
    }

    const data = await res.json();
    const row = Array.isArray(data) ? data[0] : data;
    if (!row) {
      throw new Error(`Lead ${payload.leadId} não encontrado.`);
    }

    return mapSubmissionToAdminLead(row);
  }

  async createLeadNote(payload: CreateLeadNotePayload): Promise<LeadHistoryEntry> {
    const headers = await this.getAuthHeaders();
    headers['Prefer'] = 'return=representation';

    let currentUserId: string | null = null;
    if (typeof supabase !== 'undefined') {
      const { data: userData } = await supabase.auth
        .getUser()
        .catch(() => ({ data: { user: null } }));
      currentUserId = userData?.user?.id || null;
    }

    const url = `${this.baseUrl}/rest/v1/lead_status_history`;
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        submission_id: payload.leadId,
        from_status: payload.currentStatus,
        to_status: payload.currentStatus,
        notes: payload.notes,
        ...(currentUserId ? { changed_by: currentUserId } : {}),
      }),
    });

    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        throw new Error('Acesso negado: Perfil sem permissão para salvar anotação.');
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || err.error || `Erro ao salvar anotação (HTTP ${res.status}).`);
    }

    const data = await res.json();
    const row = Array.isArray(data) ? data[0] : data;

    return {
      id: row?.id || `hist-${Date.now()}`,
      submissionId: payload.leadId,
      leadId: payload.leadId,
      fromStatus: payload.currentStatus,
      toStatus: payload.currentStatus,
      notes: payload.notes,
      changedBy: 'Israel Costa',
      createdAt: row?.created_at || new Date().toISOString(),
      timestamp: row?.created_at || new Date().toISOString(),
      field: 'notes',
      oldValue: payload.currentStatus,
      newValue: payload.notes,
    };
  }

  async recordAuditLog(payload: CreateAuditLogPayload): Promise<{ success: boolean }> {
    try {
      const headers = await this.getAuthHeaders();
      headers['Prefer'] = 'return=minimal';

      let currentUserId: string | null = null;
      if (typeof supabase !== 'undefined') {
        const { data: userData } = await supabase.auth
          .getUser()
          .catch(() => ({ data: { user: null } }));
        currentUserId = userData?.user?.id || null;
      }

      const url = `${this.baseUrl}/rest/v1/audit_logs`;
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          ...(currentUserId ? { actor_id: currentUserId } : {}),
          action: payload.action,
          resource_type: payload.resourceType,
          resource_id: payload.resourceId || null,
          metadata: payload.metadata || {},
        }),
      });

      return { success: res.ok };
    } catch {
      return { success: false };
    }
  }
}

// ------------------------------------------------------------------------------
// Exportação Principal e Fábrica
// ------------------------------------------------------------------------------
const isProduction =
  typeof import.meta !== 'undefined' &&
  import.meta.env?.VITE_APP_ENV === 'production';

export const adminApi: AdminApiClient = isProduction
  ? new HttpAdminApiClient()
  : new MockAdminApiAdapter();

export function createAdminApi(options?: {
  useMock?: boolean;
  baseUrl?: string;
  anonKey?: string;
  getAccessToken?: () => Promise<string | null>;
}): AdminApiClient {
  if (options?.useMock ?? true) {
    return new MockAdminApiAdapter();
  }
  return new HttpAdminApiClient({
    baseUrl: options?.baseUrl,
    anonKey: options?.anonKey,
    getAccessToken: options?.getAccessToken,
  });
}
